import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { catchError, forkJoin, map, Observable, of, shareReplay, switchMap, tap } from 'rxjs';
import { BASE_URL } from '@kikkz/shared/config/util';
import { LOGGING_SERVICE } from '@kikkz/shared/logging/util';
import {
  Fixture,
  firstRankingFilterOption,
  FixtureMatchStats,
  GameScoreValues,
  GroupDetail,
  HallOfFameStats,
  IPredictionGameService,
  MatchdayWinners,
  PREDICTION_GAME_TENANT_CONFIG,
  Player,
  RankingFilterOption,
  RankingPage,
  RankingUserDetail,
  UserGroup,
} from '@kikkz/shared/prediction-game/util';
import type { components } from '../api/backend-types';
import { missingIdRequest } from './missing-id';
import {
  createMapperWarn,
  MapperWarn,
  toFixture,
  toGameScoreValues,
  toGroupRankingPage,
  toHallOfFameStats,
  toPlayer,
  toRankingFilterOption,
  toRankingFilterOptionDto,
  toRankingPage,
  toRankingUserDetail,
  toUserGroup,
  toUserPredictionDto,
  toMatchdayWinners,
  byKickoff,
  byKickoffDesc,
  hasWinners,
} from './prediction-game.mapper';

type PGFixture = components['schemas']['PGFixture'];
type PGGameScoreValues = components['schemas']['PGGameScoreValues'];
type PGGroup = components['schemas']['PGGroup'];
type PGHallOfFameStats = components['schemas']['PGHallOfFameStats'];
type PGSquadPlayer = components['schemas']['PGSquadPlayer'];
type PGUserRankingList = components['schemas']['PGUserRankingList'];
type PGUserRankingListFilterOption = components['schemas']['PGUserRankingListFilterOption'];
type PGUserStats = components['schemas']['PGUserStats'];
type PGFixturePredictionGameResult = components['schemas']['PGFixturePredictionGameResult'];

const API_PREFIX = '/api/v1/predictionGame';

/**
 * Real backend implementation of {@link IPredictionGameService}.
 *
 * Maps the backend DTOs (see `../api/backend-types.ts`, generated from the OpenAPI spec)
 * onto the frontend models via `prediction-game.mapper.ts`. Known mapping gaps are
 * documented in `docs/backend-integration/prediction-game-gap-analysis.md`.
 */
@Injectable({ providedIn: 'root' })
export class HttpPredictionGameService implements IPredictionGameService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(BASE_URL);
  private readonly logger = inject(LOGGING_SERVICE);
  /** Tenant data (own club, logo overrides, scoring fallbacks) — provided by the apps layer. */
  private readonly tenant = inject(PREDICTION_GAME_TENANT_CONFIG);

  /**
   * Cached `/player` response. The squad is needed by three unrelated calls (fixtures,
   * every saved prediction, every opened profile) but changes at most a few times a
   * season, so it was refetched on every one of them. See {@link fetchSquad}.
   */
  private squad$: Observable<Player[]> | null = null;

  /**
   * Whether the request behind {@link squad$} has not answered yet. It is what makes
   * {@link loadPlayers} skip its own invalidation while a `/player` call is already in
   * flight, and it keeps the invariant that at most ONE `/player` request exists at a
   * time — so a failing request is always the one that owns the cache.
   */
  private squadPending = false;

  loadFixtures(): Observable<Fixture[]> {
    this.logger.debug('[HttpPredictionGameService] loadFixtures');
    const warn = this.mapperWarn();
    // The squad is needed to resolve goal-scorer / lineup ids into full Player objects
    // and to fill `teamToChooseFrom` (the backend does not embed it in the fixture).
    return forkJoin({
      fixtures: this.http.get<PGFixture[]>(`${this.baseUrl}${API_PREFIX}/fixture`),
      squad: this.fetchSquad(),
    }).pipe(
      map(({ fixtures, squad }) =>
        (fixtures ?? []).map((dto) => toFixture(dto, squad, this.tenant, warn)).sort(byKickoff),
      ),
    );
  }

  loadPlayers(): Observable<Player[]> {
    this.logger.debug('[HttpPredictionGameService] loadPlayers');
    // `loadPlayers` IS the squad's refresh path — the shell dispatches it on start and on
    // pull-to-refresh, and the season view on retry. So it drops the cache and refills it
    // from the network; every other caller reuses what it left behind.
    //
    // Except while a `/player` request is still in flight: the shell's start dispatches
    // `loadFixtures` (whose forkJoin fetches the squad) and `loadPlayers` in the same
    // tick, and a response that has not even arrived yet cannot be stale. Dropping it
    // here would fire a second identical request on every app start.
    if (!this.squadPending) this.squad$ = null;
    return this.fetchSquad();
  }

  loadGameScoreValues(): Observable<GameScoreValues> {
    this.logger.debug('[HttpPredictionGameService] loadGameScoreValues');
    return this.http
      .get<PGGameScoreValues>(`${this.baseUrl}${API_PREFIX}/gameScoreValues`)
      .pipe(map((dto) => toGameScoreValues(dto, this.tenant)));
  }

  savePrediction(fixtureId: string, prediction: FixtureMatchStats): Observable<Fixture> {
    this.logger.debug(`[HttpPredictionGameService] savePrediction ${fixtureId}`);
    if (!fixtureId) return this.missingId('fixtureId', 'savePrediction');
    const warn = this.mapperWarn();
    return forkJoin({
      fixture: this.http.put<PGFixture>(
        `${this.baseUrl}${API_PREFIX}/fixture/${fixtureId}`,
        toUserPredictionDto(prediction),
      ),
      squad: this.fetchSquad(),
    }).pipe(map(({ fixture, squad }) => toFixture(fixture, squad, this.tenant, warn)));
  }

  loadRankingFilterOptions(): Observable<RankingFilterOption[]> {
    this.logger.debug('[HttpPredictionGameService] loadRankingFilterOptions');
    const warn = this.mapperWarn();
    return this.http
      .get<PGUserRankingListFilterOption[]>(`${this.baseUrl}${API_PREFIX}/ranking/option`)
      .pipe(
        map((options) =>
          (options ?? [])
            .map((option) => toRankingFilterOption(option, warn))
            .sort((a, b) => a.orderIndex - b.orderIndex),
        ),
      );
  }

  loadRanking(filterOption: RankingFilterOption): Observable<RankingPage> {
    this.logger.debug(`[HttpPredictionGameService] loadRanking ${filterOption.competition ?? 'overall'}`);
    return this.http
      .post<PGUserRankingList>(`${this.baseUrl}${API_PREFIX}/ranking`, toRankingFilterOptionDto(filterOption))
      .pipe(map((list) => toRankingPage(list, this.mapperWarn())));
  }

  loadRankingUserDetail(userId: string): Observable<RankingUserDetail> {
    this.logger.debug(`[HttpPredictionGameService] loadRankingUserDetail ${userId}`);
    // A ranking row without a user id (see the mapper's warning) must not turn into
    // `GET .../userStats/` — a different endpoint with a misleading error.
    if (!userId) return this.missingId('userId', 'loadRankingUserDetail');
    const warn = this.mapperWarn();
    // NOTE: the backend keys this endpoint by user UUID, so callers must pass the user id
    // (see RankingEntry.id), not the display name.
    // The squad is needed to resolve the predicted goal-scorer / lineup ids into
    // player names for the per-fixture prediction-detail cards.
    return forkJoin({
      stats: this.http.get<PGUserStats>(`${this.baseUrl}${API_PREFIX}/userStats/${userId}`),
      squad: this.fetchSquad(),
    }).pipe(map(({ stats, squad }) => toRankingUserDetail(stats, squad, this.tenant, warn)));
  }

  loadUserGroups(): Observable<UserGroup[]> {
    this.logger.debug('[HttpPredictionGameService] loadUserGroups');
    // The group list (PGGroup) has no aggregate stats, so derive Ø-points / member count /
    // own rank from each group's ranking (assumes the group ranking returns all members).
    //
    // The ranking request needs a filter option, and the backend accepts ONLY an option it
    // handed out itself (exact competition + title + orderIndex, else 404 / errorCode
    // 20002). The global `/ranking/option` list is used here instead of each group's own
    // `/group/{id}/ranking/option`: by construction the backend derives both from the same
    // season periods and competitions, so they are identical — one request instead of N.
    const warn = this.mapperWarn();
    return forkJoin({
      groups: this.http.get<PGGroup[]>(`${this.baseUrl}${API_PREFIX}/group`),
      options: this.loadRankingFilterOptions(),
    }).pipe(
      switchMap(({ groups, options }) => {
        const list = groups ?? [];
        if (!list.length) return of([] as UserGroup[]);
        const filterOption = firstRankingFilterOption(options);
        // No option at all (season not started yet) — show the groups without statistics
        // rather than failing the whole tab on a request the backend would reject anyway.
        if (!filterOption) return of(list.map((group) => toUserGroup(group, warn)));
        return forkJoin(
          list.map((group) =>
            this.fetchGroupRanking(group.id ?? '', filterOption).pipe(
              map((ranking) => toUserGroup(group, warn, toGroupRankingPage(ranking, warn))),
              // One unreachable group ranking (or one missing its id) must not take the
              // whole list down with it.
              catchError((error: unknown) => {
                this.logger.warn(
                  `[HttpPredictionGameService] group ranking failed for ${group.id ?? '?'}: ${String(error)}`,
                );
                return of(toUserGroup(group, warn));
              }),
            ),
          ),
        );
      }),
    );
  }

  loadMatchdayWinners(): Observable<MatchdayWinners[]> {
    this.logger.debug('[HttpPredictionGameService] loadMatchdayWinners');
    const warn = this.mapperWarn();
    return this.http
      .get<PGFixturePredictionGameResult[]>(`${this.baseUrl}${API_PREFIX}/fixture/predictionGameResult`)
      .pipe(
        map((list) =>
          (list ?? [])
            .map((dto) => toMatchdayWinners(dto, this.tenant, warn))
            .filter(hasWinners)
            // Newest first: the list looks backwards, so the most recent matchday is the
            // one the user came for.
            .sort(byKickoffDesc),
        ),
      );
  }

  loadGroupRankingFilterOptions(groupId: string): Observable<RankingFilterOption[]> {
    this.logger.debug(`[HttpPredictionGameService] loadGroupRankingFilterOptions ${groupId}`);
    if (!groupId) return this.missingId('groupId', 'loadGroupRankingFilterOptions');
    const warn = this.mapperWarn();
    return this.http
      .get<PGUserRankingListFilterOption[]>(`${this.baseUrl}${API_PREFIX}/group/${groupId}/ranking/option`)
      .pipe(
        map((options) =>
          (options ?? [])
            .map((option) => toRankingFilterOption(option, warn))
            .sort((a, b) => a.orderIndex - b.orderIndex),
        ),
      );
  }

  joinGroup(code: string): Observable<UserGroup> {
    this.logger.debug(`[HttpPredictionGameService] joinGroup ${code}`);
    const params = new HttpParams().set('code', code);
    // The join response is the bare PGGroup (no members/rank) — fetch the ranking right away
    // so the overview row shows the correct member count without a manual page refresh.
    return this.http
      .post<PGGroup>(`${this.baseUrl}${API_PREFIX}/group/join`, null, { params })
      .pipe(switchMap((group) => this.fetchGroupStats(group)));
  }

  createGroup(name: string): Observable<UserGroup> {
    this.logger.debug(`[HttpPredictionGameService] createGroup ${name}`);
    return this.http.post<PGGroup>(`${this.baseUrl}${API_PREFIX}/group`, { name }).pipe(
      switchMap((group) => this.fetchGroupStats(group)),
      // The creator is the only member: even if the ranking is not materialised yet
      // (rankings refresh every ~10 min), show "1 Mitglied / Rang 1." instead of "0 / –".
      map((group) => ({ ...group, memberCount: Math.max(group.memberCount, 1), myRank: group.myRank ?? 1 })),
    );
  }

  /**
   * Enriches a bare backend group with the stats derived from its ranking. The group's own
   * filter options have to be fetched first: the ranking endpoint resolves a ranking only
   * by an option the server itself handed out (404 / errorCode 20002 otherwise).
   */
  private fetchGroupStats(group: PGGroup): Observable<UserGroup> {
    const groupId = group.id ?? '';
    const warn = this.mapperWarn();
    return this.loadGroupRankingFilterOptions(groupId).pipe(
      switchMap((options) => {
        const filterOption = firstRankingFilterOption(options);
        // Season not started yet: no option exists, so there is no ranking to derive
        // stats from — return the group itself instead of failing join/create.
        if (!filterOption) return of(toUserGroup(group, warn));
        return this.fetchGroupRanking(groupId, filterOption).pipe(
          map((ranking) => toUserGroup(group, warn, toGroupRankingPage(ranking, warn))),
        );
      }),
    );
  }

  leaveGroup(groupId: string): Observable<void> {
    this.logger.debug(`[HttpPredictionGameService] leaveGroup ${groupId}`);
    if (!groupId) return this.missingId('groupId', 'leaveGroup');
    return this.http.delete<void>(`${this.baseUrl}${API_PREFIX}/group/${groupId}/leave`).pipe(map(() => undefined));
  }

  loadGroupDetail(groupId: string, filterOption: RankingFilterOption): Observable<GroupDetail> {
    this.logger.debug(`[HttpPredictionGameService] loadGroupDetail ${groupId} ${filterOption.title}`);
    if (!groupId) return this.missingId('groupId', 'loadGroupDetail');
    const warn = this.mapperWarn();
    // The ranking endpoint returns only the ranking; group name/invite-code come from the group list.
    return forkJoin({
      groups: this.http.get<PGGroup[]>(`${this.baseUrl}${API_PREFIX}/group`),
      ranking: this.fetchGroupRanking(groupId, filterOption),
    }).pipe(
      map(({ groups, ranking }) => {
        const group = (groups ?? []).find((g) => g.id === groupId);
        return {
          id: groupId,
          name: group?.name ?? '',
          inviteCode: group?.code ?? '',
          ranking: toGroupRankingPage(ranking, warn),
        };
      }),
    );
  }

  loadHallOfFameStats(): Observable<HallOfFameStats> {
    this.logger.debug('[HttpPredictionGameService] loadHallOfFameStats');
    const warn = this.mapperWarn();
    return this.http
      .get<PGHallOfFameStats>(`${this.baseUrl}${API_PREFIX}/hallOfFameStats`)
      .pipe(map((dto) => toHallOfFameStats(dto, warn)));
  }

  // Like the global `/ranking`: the selected filter option travels as the POST body. It is
  // deliberately required — the backend matches it against the exact options it delivered,
  // so there is no default the frontend could safely fill in.
  private fetchGroupRanking(groupId: string, filterOption: RankingFilterOption): Observable<PGUserRankingList> {
    if (!groupId) return this.missingId('groupId', 'fetchGroupRanking');
    return this.http.post<PGUserRankingList>(
      `${this.baseUrl}${API_PREFIX}/group/${groupId}/ranking`,
      toRankingFilterOptionDto(filterOption),
    );
  }

  /**
   * The squad, fetched at most once per service instance until something invalidates it.
   *
   * `shareReplay` with `refCount: false` on purpose: every caller here is a short-lived
   * `forkJoin` that completes immediately, so a ref-counted share would drop the buffer
   * between two calls and refetch anyway. A failed request is explicitly NOT kept — the
   * error handler clears the field, so the next caller retries instead of replaying the
   * error forever (`shareReplay` would otherwise hand the cached error to every one of
   * them).
   *
   * Invalidation is exactly one rule: {@link loadPlayers} (the refresh path) clears a
   * SETTLED cache. Both writes to `squad$` are guarded by an identity check against the
   * request that is performing them, so a request can only ever clear its own cache
   * entry, never one that replaced it in the meantime.
   */
  private fetchSquad(): Observable<Player[]> {
    if (this.squad$) return this.squad$;
    const warn = this.mapperWarn();
    this.squadPending = true;
    const request$: Observable<Player[]> = this.http.get<PGSquadPlayer[]>(`${this.baseUrl}${API_PREFIX}/player`).pipe(
      map((players) => (players ?? []).map((player) => toPlayer(player, warn))),
      tap({
        next: () => {
          if (this.squad$ === request$) this.squadPending = false;
        },
        error: () => {
          if (this.squad$ !== request$) return;
          this.squad$ = null;
          this.squadPending = false;
        },
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    this.squad$ = request$;
    return request$;
  }

  /**
   * One warn channel per request: deduplicates identical complaints about a single
   * response (a season's worth of fixtures shares one unknown enum value) while still
   * letting the next request report the same problem again.
   */
  private mapperWarn(): MapperWarn {
    return createMapperWarn(this.logger);
  }

  /** See {@link missingIdRequest} — the shared rule, bound to this service's name. */
  private missingId<T>(what: string, method: string): Observable<T> {
    return missingIdRequest<T>('HttpPredictionGameService', method, what, this.logger);
  }
}
